import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { syncFieldQueue } from "@/hooks/useFieldQueue";
import { syncVisitTicks } from "@/hooks/useVisitQueue";
import { inventory, expireSnapshots, clearSyncedCaches, parkUnsynced, restoreParked, discardUnsynced } from "@/lib/offlineStore";

const AuthContext = createContext(null);
const notifyOffline = () => window.dispatchEvent(new Event("qs-offline-changed"));

const onSignedIn = (u) => {
  expireSnapshots();
  const n = restoreParked(u.id);
  if (n) { notifyOffline(); toast.success(`${n} unsynced record${n > 1 ? "s" : ""} kept on this device restored to your queue`); }
  return u;
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon
  const [pendingLogout, setPendingLogout] = useState(null); // inventory when unsynced work needs a decision
  const [logoutBusy, setLogoutBusy] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser((u) => u || onSignedIn(r.data))).catch(() => setUser((u) => u ?? false));
    const sweep = () => { if (document.visibilityState === "visible" && expireSnapshots()) notifyOffline(); };
    document.addEventListener("visibilitychange", sweep);
    return () => document.removeEventListener("visibilitychange", sweep);
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(onSignedIn(data.user));
    return data.user;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data.user);
    return data.user;
  }, []);

  const adoptSession = useCallback((u) => setUser(onSignedIn(u)), []);

  // mode: undefined → prompt if unsynced work exists · "sync" | "keep" | "discard". beforeClear runs (e.g. navigate home) right before the session is dropped. Returns true once signed out.
  const logout = useCallback(async (mode, beforeClear) => {
    const inv = inventory();
    if (!mode && inv.unsynced > 0) { setPendingLogout(inv); return false; }
    setLogoutBusy(true);
    try {
      if (mode === "sync") {
        if (!navigator.onLine) { setPendingLogout({ ...inv, error: "You are offline — keep the records on this device or discard them" }); return false; }
        const a = await syncFieldQueue(), b = await syncVisitTicks(null);
        notifyOffline();
        if (a.pending + b.pending > 0) { setPendingLogout({ ...inventory(), error: `${a.pending + b.pending} record(s) could not be synced — retry, keep or discard` }); return false; }
        toast.success(`${a.synced + b.synced} record(s) synced before sign-out`);
      }
      if (mode === "keep" && user) parkUnsynced(user.id);
      if (mode === "discard") discardUnsynced();
      try { await api.post("/auth/logout"); } catch (e) { console.warn("Logout request failed; clearing local session", e?.message); }
      clearSyncedCaches();
      notifyOffline();
      setPendingLogout(null);
      setSigningOut(true); // ProtectedRoute shows a spinner (not /login) while the router transitions home
      beforeClear?.();
      setUser(false);
      setTimeout(() => setSigningOut(false), 1500);
      return true;
    } finally { setLogoutBusy(false); }
  }, [user]);

  const cancelLogout = useCallback(() => setPendingLogout(null), []);

  const refreshUser = useCallback(async () => {
    const r = await api.get("/auth/me");
    setUser(r.data);
    return r.data;
  }, []);

  const value = useMemo(() => ({ user, login, register, logout, cancelLogout, pendingLogout, logoutBusy, signingOut, adoptSession, refreshUser }), [user, login, register, logout, cancelLogout, pendingLogout, logoutBusy, signingOut, adoptSession, refreshUser]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
